// src/components/SettingsModal.js
import React, { useState, useEffect } from 'react';
import ToggleSwitch from './ToggleSwitch';
import { linkEmailPassword, activatePromoCode, updateDonationSettings } from '../services/api';
import Avatar from './Avatar';
import { themeManager } from '../utils/themeManager';

const applyWallpaper = themeManager.applyWallpaper;

const THEMES = window.HatchupThemes ? window.HatchupThemes.list() : [];

export default function SettingsModal({ user, profile, onLogout, onUpdate }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState('');
    const [promoCode, setPromoCode] = useState('');
    const [promoMessage, setPromoMessage] = useState('');

    // --- НАСТРОЙКИ ТЕМЫ И ОБОЕВ ---
    const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem('hatchup_theme') || 'default');
    const [bgMode, setBgMode] = useState(() => localStorage.getItem('hatchup_bg_mode') || 'none');
    const [customBgUrl, setCustomBgUrl] = useState(() => localStorage.getItem('hatchup_custom_bg_url') || '');
    
    // Новое состояние для затемнения (по умолчанию 65%)
    const [bgDim, setBgDim] = useState(() => {
        const saved = localStorage.getItem('hatchup_bg_dim');
        return saved !== null ? Number(saved) : 65;
    });

    // --- ДОНАТЫ ---
    const [donationsEnabled, setDonationsEnabled] = useState(profile?.donationsEnabled ?? true);
    const [minDonation, setMinDonation] = useState(profile?.minDonation ?? 10);
    const [minTts, setMinTts] = useState(profile?.minTtsDonation ?? 100);

    useEffect(() => {
        if (profile) {
            setDonationsEnabled(profile.donationsEnabled);
            setMinDonation(profile.minDonation);
            setMinTts(profile.minTtsDonation);
        }
    }, [profile]);

    // Смена темы
    const handleThemeChange = (themeId) => {
        setCurrentTheme(themeId);
        if (themeId === 'default') {
            document.documentElement.removeAttribute('data-theme');
        } else {
            document.documentElement.setAttribute('data-theme', themeId);
        }
        localStorage.setItem('hatchup_theme', themeId);
        applyWallpaper(themeId, bgMode, customBgUrl, bgDim);
    };

    // Смена режима фона
    const handleBgModeChange = (mode) => {
        setBgMode(mode);
        localStorage.setItem('hatchup_bg_mode', mode);
        applyWallpaper(currentTheme, mode, customBgUrl, bgDim);
    };

    // Изменение ползунка затемнения
    const handleDimChange = (e) => {
        const val = Number(e.target.value);
        setBgDim(val);
        localStorage.setItem('hatchup_bg_dim', val);
        applyWallpaper(currentTheme, bgMode, customBgUrl, val);
    };

    // Сохранение своего фона
    const handleCustomBgSave = (e) => {
        e.preventDefault();
        localStorage.setItem('hatchup_custom_bg_url', customBgUrl);
        applyWallpaper(currentTheme, 'custom', customBgUrl, bgDim);
    };

    const handleUpdateDonations = async (updatedFields) => {
        try {
            await updateDonationSettings({
                enabled: updatedFields.enabled ?? donationsEnabled,
                minDonation: updatedFields.minDonation ?? minDonation,
                minTtsDonation: updatedFields.minTtsDonation ?? minTts
            });
            if (onUpdate) onUpdate();
        } catch (error) {
            alert('Ошибка при сохранении настроек доната');
        }
    };

    const copyLink = (stream = false) => {
        const url = `https://points.hatchup.ru/donate/${user.id}${stream ? '?stream' : ''}`;
        navigator.clipboard.writeText(url);
        alert('Ссылка скопирована!');
    };

    const handleActivateCode = async (e) => {
        e.preventDefault();
        setPromoMessage('Активация...');
        try {
            const res = await activatePromoCode(promoCode);
            setPromoMessage(res.data.message);
            if (onUpdate) onUpdate();
        } catch (error) {
            setPromoMessage(error.response?.data?.error || 'Ошибка');
        }
    };

    const handleLinkSubmit = async (e) => {
        e.preventDefault();
        setMessage('Привязка...');
        try {
            await linkEmailPassword({ email, password });
            setMessage('Email успешно привязан!');
            if (onUpdate) onUpdate();
        } catch (error) {
            setMessage(error.response?.data?.error || 'Произошла ошибка.');
        }
    };

    return (
        <div className="settings-modal">
            <h2>Настройки профиля</h2>

            <div className="settings-profile-card">
                <Avatar user={user} xp={profile?.totalXp} className="avatar-large" />
                <span className="username-large">{user.serverNickname || user.username}</span>
            </div>

            {/* --- ВЫБОР ТЕМЫ --- */}
            <div className="settings-section">
                <h5>Тема оформления</h5>
                <div className="theme-picker-grid">
                    {THEMES.map((theme) => {
                        const isActive = currentTheme === theme.id;
                        return (
                            <button
                                key={theme.id}
                                type="button"
                                className={`theme-btn ${isActive ? 'active' : ''}`}
                                onClick={() => handleThemeChange(theme.id)}
                            >
                                <span className="theme-color-dot" style={{ background: theme.color }} />
                                <span className="theme-name">{theme.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* --- СИСТЕМА ФОНОВ И ЗАТЕМНЕНИЯ --- */}
            <div className="settings-section">
                <h5>Фон сайта (Обои)</h5>
                <div className="wallpaper-modes-grid">
                    <button
                        type="button"
                        className={`wallpaper-mode-btn ${bgMode === 'none' ? 'active' : ''}`}
                        onClick={() => handleBgModeChange('none')}
                    >
                        🚫 Без фона
                    </button>
                    <button
                        type="button"
                        className={`wallpaper-mode-btn ${bgMode === 'theme' ? 'active' : ''}`}
                        onClick={() => handleBgModeChange('theme')}
                    >
                        🎨 Фон темы
                    </button>
                    <button
                        type="button"
                        className={`wallpaper-mode-btn ${bgMode === 'custom' ? 'active' : ''}`}
                        onClick={() => handleBgModeChange('custom')}
                    >
                        🖼️ Свой фон
                    </button>
                </div>

                {/* Поле ввода ссылки для своего фона */}
                {bgMode === 'custom' && (
                    <form onSubmit={handleCustomBgSave} className="custom-bg-form">
                        <input
                            type="url"
                            value={customBgUrl}
                            onChange={(e) => setCustomBgUrl(e.target.value)}
                            placeholder="Вставьте ссылку на картинку (https://...)"
                            required
                        />
                        <button type="submit">Применить ссылку</button>
                    </form>
                )}

                {/* Ползунок регулировки затемнения (активен, если включен фон) */}
                {bgMode !== 'none' && (
                    <div className="dimmer-control-card">
                        <div className="dimmer-header">
                            <label htmlFor="bg-dim-slider">Затемнение фона</label>
                            <span className="dimmer-value">{bgDim}%</span>
                        </div>
                        <input
                            id="bg-dim-slider"
                            type="range"
                            min="0"
                            max="95"
                            step="5"
                            value={bgDim}
                            onChange={handleDimChange}
                            className="custom-range-slider"
                        />
                    </div>
                )}
            </div>

            {/* --- СЕКЦИЯ: ДОНАТЫ --- */}
            <div className="settings-section">
                <h4>Система донатов</h4>
                <div className="setting-item">
                    <label>Принимать поинты</label>
                    <ToggleSwitch
                        isToggled={donationsEnabled}
                        onToggle={() => {
                            const newVal = !donationsEnabled;
                            setDonationsEnabled(newVal);
                            handleUpdateDonations({ enabled: newVal });
                        }}
                    />
                </div>

                {donationsEnabled && (
                    <div className="donation-settings-grid">
                        <div className="input-group-vertical">
                            <label>Мин. донат</label>
                            <input 
                                type="number" 
                                value={minDonation} 
                                onChange={e => setMinDonation(e.target.value)}
                                onBlur={() => handleUpdateDonations({ minDonation })} 
                            />
                        </div>
                        <div className="input-group-vertical">
                            <label>Мин. для TTS</label>
                            <input 
                                type="number" 
                                value={minTts} 
                                onChange={e => setMinTts(e.target.value)}
                                onBlur={() => handleUpdateDonations({ minTtsDonation: minTts })} 
                            />
                        </div>
                        <div className="donation-buttons">
                            <button className="secondary-btn" onClick={() => copyLink(false)}>🔗 Ссылка на донат</button>
                        </div>
                    </div>
                )}
            </div>

            {/* --- СЕКЦИЯ: ПРОМОКОДЫ --- */}
            <div className="settings-section">
                <h5>Активировать промокод</h5>
                <form onSubmit={handleActivateCode} className="promo-code-form">
                    <input 
                        type="text" 
                        value={promoCode} 
                        onChange={e => setPromoCode(e.target.value.toUpperCase())} 
                        placeholder="Введите код" 
                        required 
                    />
                    <button type="submit">Применить</button>
                </form>
                {promoMessage && <p className="form-message">{promoMessage}</p>}
            </div>

            {/* --- СЕКЦИЯ: ПРИВЯЗКА EMAIL --- */}
            <div className="settings-section">
                <h5>Вход по Email</h5>
                {profile.hasEmail ? (
                    <p className="email-linked-message">Ваш Email уже привязан.</p>
                ) : (
                    <form onSubmit={handleLinkSubmit} className="link-email-form">
                        <p>Создайте пароль, чтобы входить без Discord.</p>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" required />
                        <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Пароль" required />
                        <button type="submit">Привязать</button>
                        {message && <p className="form-message">{message}</p>}
                    </form>
                )}
            </div>

            <div className="settings-footer">
                <button onClick={onLogout} className="logout-button-danger">
                    Выйти из профиля
                </button>
            </div>
        </div>
    );
}